import { OptionDataPayload } from '../options';
import { RaffleTicketStatus } from '../orders/raffle';
import { yearDiff } from '../util/time';
import { BreakGlassCredentials } from './breakGlass';

interface Address {
  city: string;
  country: string;
  line1: string;
  line2: string | null;
  state: string;
  zipcode: string;
}

export interface UserReferenceModel {
  id: number;
  username: string | null;
  firstName: string | null;
  lastName: string | null;
  preferredName: string | null;
  // This only indicates if the user is created without firstname/lastname
  // and is marked as a child
  // The user can have this set to false, but the birthday be as that of a child
  child: boolean;
}

export interface BaseUser extends UserReferenceModel {
  // Pii fields
  email?: string | null;
  phone?: string | null;
  address?: Address;
  bornAt: Date | null;
  profilePictureUrl: string;
}

export interface BaseUserWithAddress extends BaseUser {
  addressCity: string;
  addressCountry: string;
  addressLine1: string;
  addressLine2: string | null;
  addressState: string;
  addressZipcode: string;
}

export interface UserRegistration {
  createdAt: Date;
  updatedAt: Date;
  id: number;
  userId: number;
  attendanceType: {
    id: number;
    name: string;
    displayName: string | null;
    grantExpiresOn?: Date;
    raffleId?: number;
  };

  badgeId: number;
  badgeName: string;
  badgeArtId: number | null;
  badgePrinted: boolean;
  flags: number[];
  options: OptionDataPayload;
  raffleTicket?: RegistrationTicket;
  paidOrderItem: {
    orderItemId: number;
    dispatchDate?: Date;
    orderId: number;
    productId: number;
    name: string;
    displayName: string | null;
  } | null;
  expiresOn?: Date;
  uuid: string;
}

export interface UserAllRegistrations {
  users: BaseUserWithAddress[];
  registrations: UserRegistration[];
}

export interface RegistrationTicket {
  status: RaffleTicketStatus;
  points: number;
}

export interface CurrentUser extends UserReferenceModel {
  email: string;
  phone: string;
  addressLine1: string;
  addressLine2: string | null;
  addressCity: string;
  addressZipcode: string;
  addressCountry: string;
  addressState: string;
  bornAt: Date | null;
  newsletter: boolean;
  identityVerified: boolean;
  isVolunteer: boolean;
  permissions: string[];
  profilePictureUrl: string;
  supportAgent?: BreakGlassCredentials;
  isDepartmentLead: boolean;
  emailVerified: boolean;
}

export const currentUserHasAddress = (user: CurrentUser): boolean => {
  return (
    user.addressLine1 !== '' &&
    user.addressCity !== '' &&
    user.addressZipcode !== '' &&
    user.addressCountry !== '' &&
    user.addressState !== ''
  );
};

export const isMinorAttendee = (
  bornAt: Date,
  conventionStart: Date,
  ageThreshold: number,
): boolean => {
  const now = new Date();
  if (conventionStart > now) {
    // If the convention has already started, we want to use the current date
    conventionStart = now;
  }

  return yearDiff(bornAt, conventionStart) < ageThreshold;
};
